import posthog from "posthog-js";
import { bridge, BridgeChannels, BridgeActions } from "../bridge";

export interface Rewardful {
  referral?: string;
}

export function getRewardfulClient() {
  return (window as any).Rewardful as Rewardful;
}

// ============================================================
// Facebook Ads Attribution Debugging
// ============================================================

export interface FacebookAttributionData {
  // Facebook Click ID - primary attribution parameter
  fbclid: string | null;

  // Google Click ID — stored alongside so Google campaigns get per-ad
  // attribution through the same pipeline
  gclid: string | null;

  // UTM parameters
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;

  // Additional FB parameters
  fb_source: string | null;

  // Referrer info
  referrer: string;
  isFromFacebook: boolean;

  // Page info
  landingPage: string;
  fullUrl: string;

  // Timing
  timestamp: string;

  // All URL parameters (for debugging unknown params)
  allParams: Record<string, string>;
}

/**
 * Captures and logs all Facebook attribution data from the current URL
 */
export function captureFacebookAttribution(): FacebookAttributionData {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer;

  // Extract all URL parameters
  const allParams: Record<string, string> = {};
  params.forEach((value, key) => {
    allParams[key] = value;
  });

  // Check if referrer is from Facebook
  const facebookDomains = [
    'facebook.com',
    'fb.com',
    'fb.me',
    'm.facebook.com',
    'l.facebook.com',
    'lm.facebook.com',
    'instagram.com',  // Instagram is owned by Meta/FB
  ];

  const utmSource = params.get('utm_source')?.toLowerCase() ?? '';
  const isFromFacebook =
    facebookDomains.some(domain => referrer.includes(domain)) ||
    params.has('fbclid') ||
    utmSource.includes('facebook') ||
    utmSource.includes('fb') ||
    utmSource.includes('instagram') ||
    utmSource.includes('meta');

  const data: FacebookAttributionData = {
    // Facebook Click ID
    fbclid: params.get('fbclid'),
    gclid: params.get('gclid'),

    // UTM parameters
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),

    // Additional FB parameters
    fb_source: params.get('fb_source'),

    // Referrer info
    referrer,
    isFromFacebook,

    // Page info
    landingPage: window.location.pathname,
    fullUrl: window.location.href,

    // Timing
    timestamp: new Date().toISOString(),

    // All params for debugging
    allParams,
  };

  return data;
}

/**
 * Logs Facebook attribution data to console and tracking systems
 */
export function logFacebookAttribution(): FacebookAttributionData | null {
  const data = captureFacebookAttribution();

  // Anything worth attributing? Any utm_* param or an ad click id — not just
  // Meta; Google/Reddit UTMs and gclid too.
  const hasAttribution = Boolean(
    data.fbclid || data.gclid
    || Object.keys(data.allParams).some((k) => k.toLowerCase().startsWith('utm_')),
  );

  // Store first-party for signup: sessionStorage (this visit) + a first-touch
  // localStorage copy that survives the session for later-day converters.
  // No cookies, no third parties — read back at onboarding save and written
  // once to signup_attribution (per-ad DB attribution). Storage runs before
  // any analytics call so a tracking failure can't cost us the attribution.
  if (hasAttribution) {
    try {
      sessionStorage.setItem('fb_attribution', JSON.stringify(data));
      if (!localStorage.getItem('first_attribution')) {
        localStorage.setItem('first_attribution', JSON.stringify(data));
      }
    } catch (e) {
      console.warn('Failed to store attribution:', e);
    }
  }

  // If from Facebook, send to tracking systems
  if (data.isFromFacebook) {
    console.log('FB attribution captured debugging...');

    // Send to PostHog
    posthog.capture('fb_attribution_captured', {
      fbclid: data.fbclid,
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      utm_campaign: data.utm_campaign,
      utm_content: data.utm_content,
      utm_term: data.utm_term,
      fb_source: data.fb_source,
      referrer: data.referrer,
      landing_page: data.landingPage,
    });
  }

  return hasAttribution ? data : null;
}

/**
 * Helper to retrieve stored Facebook attribution data
 * Useful when user signs up/converts to attach attribution to the conversion
 */
export function getStoredFacebookAttribution(): FacebookAttributionData | null {
  try {
    // This visit's touch first, then the device's first touch (older session).
    const stored = sessionStorage.getItem('fb_attribution')
      ?? localStorage.getItem('first_attribution');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn('Failed to retrieve FB attribution from sessionStorage:', e);
    return null;
  }
}

// Hosts the authenticated app runs on. Landing pages live on the apex domain,
// so sessionStorage/localStorage captured there is invisible to the app —
// attribution has to ride the URL across this origin boundary.
const APP_HOSTS = ['app.outclose.com', 'app.gayborhood.io'];

const ATTRIBUTION_PARAM_KEYS = ['fbclid', 'gclid'];

/**
 * The stored attribution touch as URL params (utm_*, fbclid, gclid), for
 * forwarding across the landing → app origin boundary.
 */
export function getAttributionQueryParams(): Record<string, string> {
  const attr = getStoredFacebookAttribution();
  if (!attr) return {};
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(attr.allParams ?? {})) {
    if (key.toLowerCase().startsWith('utm_') || ATTRIBUTION_PARAM_KEYS.includes(key)) {
      params[key] = value;
    }
  }
  return params;
}

/**
 * Returns `url` with the stored attribution params (utm_*, fbclid, gclid)
 * appended; params already on the URL win. Returns the input unchanged when
 * there is nothing stored or the URL doesn't parse.
 */
export function appendAttributionParams(url: string): string {
  try {
    const parsed = new URL(url);
    for (const [key, value] of Object.entries(getAttributionQueryParams())) {
      if (!parsed.searchParams.has(key)) parsed.searchParams.set(key, value);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * Rewrites links to the app subdomain at click time, appending the stored
 * attribution params. One listener covers every landing CTA (dozens of static
 * hrefs across the landing variants) — sessionStorage doesn't cross the
 * apex → app.outclose.com origin boundary, but URL params do, where the app's
 * own capture on load picks them up again.
 */
export function installAttributionLinkForwarding(): void {
  if (APP_HOSTS.includes(window.location.hostname)) return; // already on the app origin

  const decorate = (event: MouseEvent) => {
    const anchor = (event.target as Element | null)?.closest?.('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    try {
      if (!APP_HOSTS.includes(new URL(anchor.href).hostname)) return;
      anchor.href = appendAttributionParams(anchor.href);
    } catch {
      // Malformed href — leave the link alone.
    }
  };

  // Capture phase so the href is rewritten before navigation commits;
  // auxclick covers middle-click open-in-new-tab.
  document.addEventListener('click', decorate, true);
  document.addEventListener('auxclick', decorate, true);
}

/**
 * In the native app the WebView loads with no URL params and fresh storage, so
 * web capture sees nothing. AppsFlyer on the native side resolves install
 * attribution (Meta/Google via conversion data on Android, OneLink click
 * params for web→store handoffs); this pulls it over the bridge, maps it onto
 * the same shape, and stores it as the first touch — from there the normal
 * signup-attribution pipeline applies unchanged. No-op on web and when a first
 * touch already exists.
 */
export async function loadNativeInstallAttribution(): Promise<void> {
  if (!bridge.isNative) return;
  try {
    if (localStorage.getItem('first_attribution')) return;
  } catch {
    return;
  }

  // Conversion data resolves async after the native SDK starts (on iOS only
  // after the ATT prompt), so poll a few times before giving up.
  for (const delayMs of [0, 5_000, 15_000]) {
    if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs));

    let payload: Record<string, any> | null = null;
    try {
      payload = await bridge.request(
        BridgeChannels.TRACKING,
        BridgeActions.GET_INSTALL_ATTRIBUTION,
      );
    } catch {
      return; // older app build without the handler
    }
    if (!payload) continue;

    const mediaSource: string = String(payload.media_source ?? payload.pid ?? '');
    const hasUtm = Object.keys(payload).some((k) => k.toLowerCase().startsWith('utm_'));
    const isOrganic = String(payload.af_status ?? '').toLowerCase() === 'organic';
    // A definitive organic verdict with no link params — nothing to attribute.
    if (isOrganic && !hasUtm && !mediaSource) return;

    const data: FacebookAttributionData = {
      fbclid: payload.fbclid ?? null,
      gclid: payload.gclid ?? null,
      // Prefer explicit utm_* (OneLink click params); fall back to
      // AppsFlyer's conversion fields. af_ad_id/af_ad is the per-ad grain
      // utm_content carries on web.
      utm_source: payload.utm_source ?? (mediaSource || null),
      utm_medium: payload.utm_medium ?? 'app_install',
      utm_campaign: payload.utm_campaign ?? payload.campaign ?? null,
      utm_content: payload.utm_content ?? payload.af_ad_id ?? payload.af_ad ?? null,
      utm_term: payload.utm_term ?? payload.af_adset_id ?? payload.af_adset ?? null,
      fb_source: null,
      referrer: 'app_install',
      isFromFacebook: /facebook|instagram|meta|(^|_)fb($|_)/i.test(mediaSource),
      landingPage: 'app_install',
      fullUrl: '',
      timestamp: new Date().toISOString(),
      allParams: Object.fromEntries(
        Object.entries(payload).map(([k, v]) => [k, String(v)]),
      ),
    };

    try {
      localStorage.setItem('first_attribution', JSON.stringify(data));
      if (!sessionStorage.getItem('fb_attribution')) {
        sessionStorage.setItem('fb_attribution', JSON.stringify(data));
      }
    } catch (e) {
      console.warn('Failed to store native install attribution:', e);
    }
    return;
  }
}
